import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Loader2, Pencil, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { ROLE_LABEL, isAdmin } from "@/lib/constants";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Field, NSelect, PageHeader, Spinner, UserAvatar } from "@/components/common";
import { ConfirmDialog } from "@/components/Dialogs";
import { cn } from "@/lib/utils";

const ROLE_OPTS = Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }));

function TempPwDialog({ info, onClose }) {
  return (
    <Dialog open={!!info} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle>Temporary password</DialogTitle>
          <DialogDescription>Share this with {info?.name}. They must change it on first login. It won't be shown again.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-xl bg-secondary p-4">
          <div>
            <p className="label-xs">Mobile</p>
            <p className="num font-semibold">{info?.mobile}</p>
            <p className="label-xs mt-2">Password</p>
            <p className="num text-lg font-semibold" data-testid="temp-password-value">{info?.password}</p>
          </div>
          <Button variant="outline" size="sm" data-testid="temp-password-copy" onClick={() => { navigator.clipboard?.writeText(`Mobile: ${info.mobile}\nPassword: ${info.password}`); toast.success("Copied"); }}>
            <Copy className="mr-1.5 h-4 w-4" />Copy
          </Button>
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="temp-password-done">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ open, onOpenChange, edit, admin, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const init = () => setF(edit ? { ...edit, email: edit.email || "" } : { name: "", mobile: "", email: "", role: "employee", is_partner: false });
  const s = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (edit) {
        await api.patch(`/users/${edit.id}`, { name: f.name, mobile: f.mobile, email: f.email, role: f.role, is_partner: f.is_partner, color: f.color, active: f.active });
        toast.success("User updated");
        onSaved();
      } else {
        const { data } = await api.post("/users", { name: f.name, mobile: f.mobile, email: f.email || null, role: f.role, is_partner: f.is_partner });
        onSaved({ name: data.user.name, mobile: data.user.mobile, password: data.temp_password });
      }
      onOpenChange(false);
    } catch (ex) {
      toast.error(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) init(); onOpenChange(o); }}>
      <DialogContent onOpenAutoFocus={init} data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{edit ? `Edit ${edit.name}` : admin ? "Add user" : "Add employee"}</DialogTitle>
          {!admin && <DialogDescription>Partners can add employees. Roles are managed by the Admin.</DialogDescription>}
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Name" required><Input data-testid="user-name-input" value={f.name || ""} onChange={(e) => s("name")(e.target.value)} required /></Field>
          <Field label="Mobile" required><Input data-testid="user-mobile-input" inputMode="numeric" value={f.mobile || ""} onChange={(e) => s("mobile")(e.target.value)} required /></Field>
          <Field label="Email (optional, for password reset)"><Input data-testid="user-email-input" type="email" value={f.email || ""} onChange={(e) => s("email")(e.target.value)} /></Field>
          {admin && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Role"><NSelect testid="user-role-select" value={f.role} onChange={s("role")} options={ROLE_OPTS} /></Field>
              <Field label="Counts as partner"><div className="flex h-10 items-center"><Switch checked={!!f.is_partner} onCheckedChange={s("is_partner")} data-testid="user-is-partner-switch" /></div></Field>
            </div>
          )}
          {edit && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Chat colour"><input type="color" data-testid="user-color-input" value={f.color || "#0EA5E9"} onChange={(e) => s("color")(e.target.value.toUpperCase())} className="h-10 w-full cursor-pointer rounded-lg border bg-card" /></Field>
              <Field label="Active"><div className="flex h-10 items-center"><Switch checked={!!f.active} onCheckedChange={s("active")} data-testid="user-active-switch" /></div></Field>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="user-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{edit ? "Save" : "Create & get password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const qc = useQueryClient();
  const [dlg, setDlg] = useState({ open: false, edit: null });
  const [tempPw, setTempPw] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const refresh = (pw) => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["users-lite"] });
    if (pw) setTempPw(pw);
  };
  const doReset = async () => {
    try {
      const { data } = await api.post(`/users/${resetFor.id}/reset-password`);
      setTempPw({ name: resetFor.name, mobile: resetFor.mobile, password: data.temp_password });
    } catch (e) {
      toast.error(formatErr(e));
    }
    setResetFor(null);
  };

  return (
    <>
      <PageHeader title="Team" testid="users-title" subtitle="Partners, accounts and employees. Users are deactivated, never deleted."
        actions={<Button size="sm" onClick={() => setDlg({ open: true, edit: null })} data-testid="users-add-button"><UserPlus className="mr-1.5 h-4 w-4" />{admin ? "Add user" : "Add employee"}</Button>} />
      {isLoading ? <Spinner /> : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-card-${u.id}`} className={cn("rounded-2xl border bg-card p-4", !u.active && "opacity-60")}>
              <div className="flex items-start gap-3">
                <UserAvatar user={u} size="lg" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{u.name}</p>
                  <p className="num text-xs text-muted-foreground">{u.mobile}{u.email ? ` · ${u.email}` : ""}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary" data-testid={`user-role-${u.id}`}>{ROLE_LABEL[u.role]}</span>
                    {u.is_partner && <span className="rounded-md bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">Partner {u.partner_order}</span>}
                    {u.is_sample && <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-semibold">SAMPLE</span>}
                    {!u.active && <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive">Inactive</span>}
                    {u.must_change_password && u.active && <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px]">Temp password</span>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between border-t pt-3">
                <span className="text-[11px] text-muted-foreground">Last login: {u.last_login_at ? fmtDateTime(u.last_login_at) : "never"}</span>
                {admin && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setResetFor(u)} data-testid={`user-reset-password-${u.id}`} title="Reset password"><KeyRound className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDlg({ open: true, edit: u })} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <UserDialog open={dlg.open} edit={dlg.edit} admin={admin} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={refresh} />
      <TempPwDialog info={tempPw} onClose={() => setTempPw(null)} />
      <ConfirmDialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)} title={`Reset password for ${resetFor?.name}?`}
        text="A new temporary password will be generated and they must change it on next login." confirmLabel="Reset password" onConfirm={doReset} testid="reset-password-dialog" />
    </>
  );
}
